/**
 * useCart — tiny hook wrapping /api/cart. Exposes `cart`, `count`,
 * `subtotal`, and mutators. Consumers can call `refresh()` after any
 * external add-to-cart action (product card, product page, etc.).
 *
 * Cart is user-scoped; a signed-out user gets an empty cart and any
 * `add` call short-circuits to a toast prompting sign-in.
 */
import { useCallback, useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const EMPTY = { items: [], totals: { count: 0, subtotal: 0, currency: "usd" } };

export function useCart() {
  const { user } = useAuth();
  const [cart, setCart] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user?.id) { setCart(EMPTY); return; }
    setLoading(true);
    try {
      const { data } = await http.get("/cart");
      setCart(data);
    } catch (e) {
      console.debug("cart refresh skipped:", e?.response?.status);
      setCart(EMPTY);
    } finally { setLoading(false); }
  }, [user?.id]);

  useEffect(() => { refresh(); }, [refresh]);

  const add = useCallback(async (productId, quantity = 1) => {
    if (!user?.id) {
      toast.error("Please sign in to add items to your cart.");
      return null;
    }
    try {
      const { data } = await http.post("/cart/items", { product_id: productId, quantity });
      setCart(data.cart);
      toast.success("Added to cart.");
      return data.cart;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to cart.");
      return null;
    }
  }, [user?.id]);

  const updateQty = useCallback(async (itemId, quantity) => {
    try {
      const { data } = await http.patch(`/cart/items/${itemId}`, { quantity });
      setCart(data.cart);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  }, []);

  const remove = useCallback(async (itemId) => {
    try {
      const { data } = await http.delete(`/cart/items/${itemId}`);
      setCart(data.cart);
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed."); }
  }, []);

  const clear = useCallback(async () => {
    try { await http.delete("/cart"); setCart(EMPTY); } catch (_) { /* noop */ }
  }, []);

  return { cart, count: cart?.totals?.count || 0, subtotal: cart?.totals?.subtotal || 0,
           currency: cart?.totals?.currency || "usd", loading, refresh, add, updateQty, remove, clear };
}
